//@flow
import React from 'react';

import {
  FILE_FORMAT_ERROR_MESSAGE,
  FILE_INPUT_1,
  FILE_INPUT_2,
  TITLE_BY_DEFAULT
} from 'constants/contactForm7Constants';
import { STATUS_SUCCESS } from 'constants/statusConstants';
import { nullFieldClasses } from 'types/ContactForm7';
import type { CustomEvent, FieldsType, InputValue, SingleContactFormType } from 'types/ContactForm7';
import type { Status } from 'types/Status';
import {
  getCF7ClassesData,
  getFieldType,
  isEmpty,
  isEmailValid,
  isFileTypeIncorrect,
  isFileSizeIncorrect
} from 'utils/contactForm7Utils';

import { fieldClasses, fieldOnASingleRow, formClasses, multipleFieldsOnASingleRow } from './CF7Classes';
import CF7Field from './CF7Field';

export type StateProps = {
  sendStatus: Status
};

export type DispatchProps = {
  postContactForm7: (formInputs: { [key: string]: InputValue }, id: number) => void
};

type Props = SingleContactFormType & DispatchProps & StateProps & { initialValues: { [key: string]: any } };

type StateErrors = { [key: string]: string };

type State = {
  authorizedFileFormatMessage: string,
  data: {
    [key: string]: InputValue
  },
  errors: StateErrors,
  formFields: FieldsType[]
};

class CF7Form extends React.Component<Props, State> {
  _form: HTMLFormElement | null;

  constructor(props: Props) {
    super(props);

    this.state = {
      authorizedFileFormatMessage: '',
      data: {},
      errors: {},
      formFields: []
    };
  }

  // Life Cycles
  componentDidMount() {
    this.setDefaultValues();
    this.addInitialValuesToFields();
    this.setAuthorizedFileFormatMessage();
  }

  componentDidUpdate(prevProps: Props) {
    const { initialValues, properties, sendStatus } = this.props;
    const { initialValues: prevInitialValues, properties: prevProperties, sendStatus: prevSendStatus } = prevProps;

    if (prevProperties !== properties) {
      this.setDefaultValues();
      this.addInitialValuesToFields();
      this.setAuthorizedFileFormatMessage();
    }

    if (initialValues !== prevInitialValues) {
      this.addInitialValuesToFields();
    }

    // reset the form when email is successfully send and reset DefaultValues
    if (prevSendStatus !== sendStatus && sendStatus === STATUS_SUCCESS) {
      this.resetForm();
    }
  }

  addInitialValuesToFields: () => void = () => {
    const { properties } = this.props;

    const formFields = this.getFormFields(properties.form.fields);

    this.setState({ formFields });
  };

  getFormFields: (fields: Array<FieldsType>) => Array<FieldsType> = (fields: FieldsType[]) => {
    const { initialValues } = this.props;
    const initialValuesKeys = Object.keys(initialValues);

    let formFields: FieldsType[] = [];
    if (typeof fields !== 'undefined' && fields.length) {
      formFields = fields
        .map(field => {
          if (field.type === 'hidden' && initialValuesKeys.includes(field.name)) {
            let values = [];
            values.push(initialValues[field.name]);
            return {
              ...field,
              values
            };
          }
          return field;
        })
        .filter(field => typeof field !== 'undefined');
    }

    return formFields;
  };

  setAuthorizedFileFormatMessage: () => void = () => {
    const { properties } = this.props;

    const fileField = properties.form.fields.find(field => field.type === 'file');

    let message = '';
    if (fileField) {
      const authorizedFileTypes = this.getAuthorizedFileTypes(fileField);

      if (authorizedFileTypes) {
        message = `extension(s) autorisée(s) : ${authorizedFileTypes}`;
      }
    }

    this.setState({ authorizedFileFormatMessage: message });
  };

  //
  setDefaultValues: () => void = () => {
    const { initialValues } = this.props;
    const { fields } = this.props.properties.form;
    const defaultData = {};

    fields.forEach(field => {
      let defaultValue = undefined;

      if (field.type.startsWith('checkbox')) {
        const defaultOption = field.options.find(opt => opt.includes('default:'));

        if (defaultOption) {
          const defaultValues = defaultOption.split(':')[1].split('_');

          defaultValues.forEach(defaultIndex => {
            const index = parseInt(defaultIndex, 10);

            if (field.options.indexOf('exclusive') !== -1) {
              defaultValue = field.labels[index - 1];
            } else {
              if (!Array.isArray(defaultValue)) {
                defaultValue = [];
              }

              defaultValue.push(field.labels[index - 1]);
            }
          });
        }
      }

      defaultData[field.name] = defaultValue;
    });

    this.setState({
      data: initialValues ? { ...defaultData, ...initialValues } : defaultData
    });
  };

  resetForm: () => void = () => {
    this.setState({ data: {}, errors: {} }, () => this.setDefaultValues());
  };

  // Form functions
  checkFormValidity: () => void = () => {
    const { fields } = this.props.properties.form;

    let { errors } = this.state;
    fields.forEach(field => {
      errors = this._checkInputValidity(errors, field.name);
    });

    this.setState({ errors });
  };

  _checkInputValidity: (prevErrors: StateErrors, name: string) => StateErrors | { [string]: string } = (
    prevErrors: StateErrors,
    name: string
  ) => {
    const field = this.getFieldByName(name);

    if (!field) {
      return prevErrors;
    }

    const value = this.state.data[field.name];
    const errorMessages = this.props.properties.messages;

    // required inputs
    if (field.type.endsWith('*') && isEmpty(value)) {
      return { ...prevErrors, [name]: errorMessages.invalid_required };
    }

    // email validation
    const isEmail = field.type.startsWith('email');
    if (isEmail && !isEmailValid(value.toString()) && !isEmpty(value)) {
      return { ...prevErrors, [name]: errorMessages.invalid_email };
    }

    // file validation
    const isFile = field.type.startsWith('file');
    if (isFile && !isEmpty(value)) {
      const input = document.querySelector(`input[name='${field.name}']`);
      // $FlowFixMe
      const file = input?.files && input.files[0];

      if (file) {
        if (isFileTypeIncorrect(file, field)) {
          const authorizedFileFormats = this.getAuthorizedFileTypes(field);
          const message = `${FILE_FORMAT_ERROR_MESSAGE} Seuls les extensions ${authorizedFileFormats} sont acceptées.`;
          return { ...prevErrors, [name]: message };
        }

        if (isFileSizeIncorrect(file, field)) {
          return { ...prevErrors, [name]: errorMessages.upload_file_too_large };
        }
      }
    }

    const newErrors = { ...prevErrors };
    delete newErrors[name];

    return newErrors;
  };

  getAuthorizedFileTypes: (field: FieldsType) => string = (field: FieldsType) => {
    const fileTypes = field.options.find(option => option.includes('filetypes'));

    let authorizedFileFormats = '';

    if (fileTypes) {
      authorizedFileFormats = fileTypes ? fileTypes.split(':')[1] : '';
      authorizedFileFormats = authorizedFileFormats ? authorizedFileFormats.split('|') : '';
      authorizedFileFormats =
        !!authorizedFileFormats && !!authorizedFileFormats.length
          ? authorizedFileFormats
              .map((format, index) => {
                if (index === 0) {
                  return `.${format}`;
                } else if (index < authorizedFileFormats.length - 1) {
                  return `, .${format}`;
                } else if (index === authorizedFileFormats.length - 1) {
                  return ` et .${format}`;
                }
              })
              .join('')
          : '';
    }

    return authorizedFileFormats;
  };

  getFieldByName: (name: string) => void | FieldsType = (name: string) => {
    return this.props.properties.form.fields.find(field => field.name === name);
  };

  handleOnSubmit: (event: CustomEvent) => void = (event: CustomEvent) => {
    event.preventDefault();

    const { errors } = this.state;

    if (errors && (Object.keys(errors).includes(FILE_INPUT_1) || Object.keys(errors).includes(FILE_INPUT_2))) {
      return;
    }

    const form = this._form;

    if (form) {
      // https://developer.mozilla.org/en-US/docs/Web/API/HTMLSelectElement/checkValidity
      // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/password - cf7 ne supporte pas l'attribut pattern
      const isValid = form.checkValidity();

      if (isValid) {
        const { id } = this.props;
        const { fields } = this.props.properties.form;
        const hiddenField = fields.find(field => field.basetype === 'hidden');

        const formData = new FormData(form);
        if (hiddenField) {
          formData.append(hiddenField.name, this.props.title ?? TITLE_BY_DEFAULT);
        }
        formData.append('_wpcf7_unit_tag', id.toString());
        /*eslint no-useless-computed-key: "off"*/
        this.props.postContactForm7(formData, id);
      }
    }
  };

  // CF7Field Functions
  handleOnChange: (event: CustomEvent) => void = (event: CustomEvent) => {
    const { name, value } = event.currentTarget;
    const field = this.getFieldByName(name);

    if (!field) return;

    const newData = { ...this.state.data, [name]: value };
    this.setState({ data: newData });
  };

  handleOnBlur: (event: CustomEvent) => void = (event: CustomEvent) => {
    const { name } = event.currentTarget;

    this.checkInputValidity(name);
  };

  onCancelForm: (event: CustomEvent) => void = (event: CustomEvent) => {
    event.preventDefault();

    this.resetForm();
  };

  // Form and CF7Field Functions
  checkInputValidity: (name: string) => void = (name: string) => {
    const field = this.getFieldByName(name);

    if (!field) {
      return;
    }

    this.setState(prevState => ({
      errors: this._checkInputValidity(prevState.errors, name)
    }));
  };

  render() {
    const { authorizedFileFormatMessage, data, formFields } = this.state;
    const { slug } = this.props;
    const formClassNames = formClasses.find(item =>
      item.formNames.includes(slug) || item.formNames.includes('') ? formClasses.classes : ''
    );

    if (data === {} || !formFields.length) {
      return null;
    }

    return (
      // onInvalid : l'event lorsque checkValidity échoue
      // TODO : au lieu de vérifier les erreurs en JS avec this.checkFormValidity, écouter les events remontés par le formulaire
      <form
        ref={form => {
          this._form = form;
        }}
        className={formClassNames}
        onInvalid={this.checkFormValidity}
        onSubmit={this.handleOnSubmit}
      >
        {formFields.reduce((acc, currentField) => {
          const fieldType = getFieldType(currentField.type);

          const hasNoData = Object.values(this.state.data).every(currentValue => currentValue === undefined);

          /* CUSTOM FIELD CLASSES */
          const fieldClassesObject = getCF7ClassesData(fieldClasses, slug, fieldType);

          /* DOM ELEMENT */
          const field = (
            <CF7Field
              key={currentField.name}
              field={currentField}
              fieldClassesObject={fieldClassesObject ?? nullFieldClasses}
              errorMessage={this.state.errors[currentField.name]}
              hasNoData={hasNoData}
              name={currentField.name}
              onChange={this.handleOnChange}
              onBlur={this.handleOnBlur}
              value={this.state.data[currentField.name]}
              onCancelForm={this.onCancelForm}
              authorizedFileFormatMessage={currentField.type === 'submit' ? authorizedFileFormatMessage : ''}
            />
          );

          /* LAYOUT EXCEPTIONS */
          // is the currentField on a single row with multiple fields
          const fieldsBelongToMultipleFieldsOnASingleRow = getCF7ClassesData(
            multipleFieldsOnASingleRow,
            slug,
            fieldType
          );

          // if not it may be alone on its row
          const fieldIsOnASingleRow = fieldsBelongToMultipleFieldsOnASingleRow
            ? false
            : getCF7ClassesData(fieldOnASingleRow, slug, fieldType);

          // if we have fields in lastArray
          const { lastArrayIndex, lastArray } = acc.reduce(
            (acc2, fieldDomElement, index) =>
              Array.isArray(fieldDomElement) ? { lastArrayIndex: index, lastArray: fieldDomElement } : acc2,
            {}
          );

          // CSS
          // for single row
          if (fieldIsOnASingleRow) {
            // without any css row class
            if (!fieldIsOnASingleRow.rowClasses) {
              acc.push(field);
            } else {
              // with a row class
              const newField = <div className={fieldIsOnASingleRow.rowClasses}>{field}</div>;

              acc.push(newField);
            }
          } else if (lastArray && lastArray.length < 2) {
            // if field on a row with multiple fields - we had it to lastArray
            lastArray.push(field);

            // if last array has two field
            if (lastArray.length === 2) {
              // we handle the row class
              const { rowClasses } = fieldsBelongToMultipleFieldsOnASingleRow
                ? {
                    rowClasses: fieldsBelongToMultipleFieldsOnASingleRow.rowClasses
                  }
                : { rowClasses: '' };

              acc[lastArrayIndex] = (
                <div key={lastArray[0].key} className={rowClasses}>
                  {lastArray.map(field => {
                    // we handle the field class
                    const currentLastArrayFieldType = field.props.field.basetype;
                    const currentLastArrayFieldCss = getCF7ClassesData(
                      multipleFieldsOnASingleRow,
                      slug,
                      currentLastArrayFieldType
                    );

                    if (currentLastArrayFieldCss && !currentLastArrayFieldCss.divContainedInRowClasses) {
                      return field;
                    }

                    return (
                      <div
                        key={lastArray[0].key}
                        className={currentLastArrayFieldCss ? currentLastArrayFieldCss.divContainedInRowClasses : ''}
                      >
                        {field}
                      </div>
                    );
                  })}
                </div>
              );
            }
          } else {
            acc.push([field]);
          }

          return acc;
        }, [])}
      </form>
    );
  }
}

export default CF7Form;
